// @flow

import type {
  UserOperation,
  UserOperationsById,
} from 'fc-operator-user/types'
import { User } from 'fc-model-users/types'

// Libs
import React, { Component } from 'react'
import _find from 'lodash/find'
import _trim from 'lodash/trim'
import _get from 'lodash/get'
import validator from 'validator'
// Utils
import traverse from 'fc-utils/traverse'

// Components
import Button from '@react-mdc/button'
import { Errors } from 'hfty-components-errors'
import { FormattedMessage } from 'react-intl'

// Messages
import messages from '../messages'
import accountsMessages from 'hfty-accounts/src/messages'

// Constants
import {
  PROP_FIRST,
  PROP_LAST,
  PROP_USERNAME,
  PROP_USER_EMAIL,
  PROP_PHONE,
} from 'fc-model-users'

// Components
import Textfield from '@react-mdc/textfield'
import FormField from '@react-mdc/form-field'

// Styles
import { error, black } from 'hfty-styles/src/colors'
import { sidebar as sidebarCommonStyle, INDENT } from 'hfty-styles/src/common'
const sidebar = sidebarCommonStyle

const styles = {
  passwordError: {
    color: error(),
  },
  input: {
    width: 300,
  },
  caseSensitiveText: {
    color: black(),
    fontSize: '12px',
    padding: '3px',
    fontWeight: 500,
  },
  buttonWrapper: {
    marginTop: INDENT,
  },
}

// Getters
const getFirst = traverse(['operation', 'model', PROP_FIRST], { default: '' })
const getLast = traverse(['operation', 'model', PROP_LAST], { default: '' })
const getUsername = traverse(['operation', 'model', PROP_USERNAME], { default: '' })
const getPhone = traverse(['operation', 'model', PROP_PHONE], { default: '' })
const getEmail = traverse(['operation', 'model', PROP_USER_EMAIL], { default: '' })

type Props = {
  operations: UserOperationsById,
  operation: UserOperation,
  users: Array,
  onChange: Function,
  onCancel: Function,
  onSubmit: Function,
  onDeleteOp: Function,
}

export default class EditUser extends Component {
  props: Props
  onChangeFirst: Function
  onChangeLast: Function
  onSubmit: Function
  onCancel: Function

  constructor (props: Props) {
    super(props)

    this.state = {
      confirmPassword: '',
      emailError: null,
      triedSubmission: false,
    }

    this.onChangeFirst = this.onChangeFirst.bind(this)
    this.onChangeLast = this.onChangeLast.bind(this)
    this.onChangeUsername = this.onChangeUsername.bind(this)
    this.onChangePhone = this.onChangePhone.bind(this)
    this.onChangeEmail = this.onChangeEmail.bind(this)
    this.onChangePassword = this.onChangePassword.bind(this)
    this.onChangeConfirmPassword = this.onChangeConfirmPassword.bind(this)
    this.onSubmit = this.onSubmit.bind(this)
    this.onCancel = this.onCancel.bind(this)
    this.render = this.render.bind(this)
    this.checkExistingEmails = this.checkExistingEmails.bind(this)
  }

  checkExistingEmails (email: string): boolean {
    const currId = this.props.operation.model.id
    return _find(this.props.users, (u: User): ?Object => u.id !== currId && u.email === email) !== undefined
  }

  isSubmitDisabled (): boolean {
    const email = _get(this.props.operation, 'model.email', '')
    const firstName = _get(this.props.operation, 'model.first', '')
    const lastName = _get(this.props.operation, 'model.last', '')
    const userName = _get(this.props.operation, 'model.username', '')
    const phone = _get(this.props.operation, 'model.phone', '')

    return !email || !firstName || !lastName || !userName || !phone
  }

  onSubmit (e: Event) {
    e.preventDefault()
    this.setState({
      triedSubmission: true,
    })
    const email = this.props.operation.model.email
    if (email && _trim(email) !== '' &&
    !validator.isEmail(email || '')) {
      this.setState({
        emailError: 'Invalid email address',
      })
    } else if (this.checkExistingEmails(email)) {
      this.setState({
        'emailError': 'This email address is already in use',
      })
    } else {
      this.setState({
        'emailError': null,
      })

      let passwd = this.props.operation.model.user ? this.props.operation.model.user.password : null
      if (passwd && passwd !== this.state.confirmPassword) {
        this.setState({
          'passwordError': 'Passwords do not match',
        })
      } else if (passwd && passwd.length < 8) {
        this.setState({
          'passwordError': 'Password must be at least 8 characters',
        })
      } else {
        this.setState({
          'passwordError': null,
        })
        this.props.onSubmit(this.props.operations)
      }
    }
  }

  onCancel (e: Event) {
    e.preventDefault()
    this.setState({
      confirmPassword: '',
      emailError: null,
      triedSubmission: false,
    })
    this.props.onCancel(this.props.operations)
  }

  onDelete (e: Event) {
    e.preventDefault()

    this.props.onDeleteOp(this.props.operations)
  }

  onChangeFirst (e: Event & { currentTarget: HTMLInputElement }) {
    this.props.onChange({
      ...this.props.operation,
      model: {
        [PROP_FIRST]: e.currentTarget.value,
      },
    })
  }

  onChangeLast (e: Event & { currentTarget: HTMLInputElement }) {
    this.props.onChange({
      ...this.props.operation,
      model: {
        [PROP_LAST]: e.currentTarget.value,
      },
    })
  }

  onChangeUsername (e: Event & { currentTarget: HTMLInputElement }) {
    this.props.onChange({
      ...this.props.operation,
      model: {
        [PROP_USERNAME]: e.currentTarget.value,
        user: {
          ...this.props.operation.model.user,
          username: e.currentTarget.value,
        },
      },
    })
  }

  onChangePhone (e: Event & { currentTarget: HTMLInputElement }) {
    this.props.onChange({
      ...this.props.operation,
      model: {
        ...this.props.operation.model,
        phone: e.currentTarget.value,
      },
    })
  }

  onChangeEmail (e: Event & { currentTarget: HTMLInputElement }) {
    const value = _trim(e.currentTarget.value || '')
    const isValidEmail = value === '' || validator.isEmail(value)
    if (!isValidEmail) {
      this.setState({
        emailError: 'Invalid email address',
      })
    } else {
      this.setState({
        emailError: null,
      })
    }
    this.props.onChange({
      ...this.props.operation,
      model: {
        ...this.props.operation.model,
        email: value,
      },
    })
  }

  onChangePassword (e: Event & { currentTarget: HTMLInputElement }) {
    this.props.onChange({
      ...this.props.operation,
      model: {
        user: {
          ...this.props.operation.model.user,
          password: e.currentTarget.value,
        },
      },
    })
  }

  onChangeConfirmPassword (e: Event & { currentTarget: HTMLInputElement }) {
    this.setState({
      confirmPassword: e.currentTarget.value,
    })
  }

  renderUsernameisCaseSentiveText (): React.Element<any> {
    return (
      <div style={styles.caseSensitiveText}>
        <FormattedMessage {...accountsMessages['usernameIsCaseSensitive']} />
      </div>
    )
  }

  render (): React.Element<any> {
    if (!this.props.operation || this.props.operation.operation === 'OTHER') {
      return null
    }
    return (
      <form style={sidebar.container} onSubmit={this.onSubmit}>
        <FormField>
          <Errors
            errors={this.props.operation.errors}
            messages={messages}
          />
        </FormField>
        <FormField>
          <Textfield disabled={this.props.operation.submitting}>
            <Textfield.Input
              id={`first-${this.props.operation.operationId}`}
              value={getFirst(this.props)}
              onChange={this.onChangeFirst}
              required
              style={styles.input}
            />
            <Textfield.Label htmlFor='usage-1'>{'First Name'}</Textfield.Label>
          </Textfield>
        </FormField>
        <FormField>
          <Textfield disabled={this.props.operation.submitting}>
            <Textfield.Input
              id={`last-${this.props.operation.operationId}`}
              value={getLast(this.props)}
              onChange={this.onChangeLast}
              required
              style={styles.input}
            />
            <Textfield.Label htmlFor='usage-1'>{'Last Name'}</Textfield.Label>
          </Textfield>
        </FormField>
        <FormField>
          <Textfield disabled={this.props.operation.submitting}>
            <Textfield.Input
              id={`phone-${this.props.operation.operationId}`}
              value={getPhone(this.props)}
              onChange={this.onChangePhone}
              style={styles.input}
              required
            />
            <Textfield.Label htmlFor='usage-1'>{'Phone'}</Textfield.Label>
          </Textfield>
        </FormField>
        <FormField>
          <Textfield disabled={this.props.operation.submitting}>
            <Textfield.Input
              id={`email-${this.props.operation.operationId}`}
              value={getEmail(this.props)}
              onChange={this.onChangeEmail}
              style={styles.input}
              required
            />
            <Textfield.Label htmlFor='usage-1'>{'Email'}</Textfield.Label>
          </Textfield>
        </FormField>
        {this.state.triedSubmission &&
          <div style={styles.passwordError}>
            {this.state.emailError}
          </div>
        }
        <FormField>
          <Textfield disabled={this.props.operation.submitting}>
            <Textfield.Input
              id={`username-${this.props.operation.operationId}`}
              value={getUsername(this.props)}
              onChange={this.onChangeUsername}
              style={styles.input}
              required
            />
            <Textfield.Label htmlFor='usage-1'>{'Username'}</Textfield.Label>
          </Textfield>
        </FormField>
        {this.renderUsernameisCaseSentiveText()}
        <FormField>
          <Textfield disabled={this.props.operation.submitting}>
            <Textfield.Input
              id={`password-${this.props.operation.operationId}`}
              onChange={this.onChangePassword}
              type={'password'}
              style={styles.input}
            />
            <Textfield.Label htmlFor='usage-1'>{'New Password'}</Textfield.Label>
          </Textfield>
        </FormField>
        <div style={styles.passwordError}>
          {this.state.passwordError}
        </div>
        <FormField>
          <Textfield disabled={this.props.operation.submitting}>
            <Textfield.Input
              id={`confirmPassword-${this.props.operation.operationId}`}
              onChange={this.onChangeConfirmPassword}
              type={'password'}
              style={styles.input}
            />
            <Textfield.Label htmlFor='usage-1'>{'Confirm New Password'}</Textfield.Label>
          </Textfield>
        </FormField>
        <div style={{...sidebar.buttonWrapper, ...styles.buttonWrapper}}>
          <Button
            raised
            primary
            disabled={this.isSubmitDisabled()}
            style={sidebar.button}
            onClick={this.onSubmit}
          >
            {'Submit'}
          </Button>
          <Button
            raised
            onClick={this.onCancel}
          >
            {'Cancel'}
          </Button>
        </div>
      </form>
    )
  }
}
